// FNP-1400 — `declarationFor` is imported so a CORRECTION and a CONNECT go
// through the same producer. `rolePolicyBuilder` imports nothing from this
// file, so the dependency runs one way and there is no cycle.
import {declarationFor, RoleGrantSelection} from './services/rolePolicyBuilder';

// FNP-1248 — mirrors the deployed FNP-1179 / FNP-1181 / FNP-1182 schema
// (flexi-domains-api/src/appsync-api/src/schema/features/provider-connections.graphql
//  and .../dns-providers.graphql).
//
// ⚠️ THE ONE RULE THAT GOVERNS EVERY TYPE IN THIS FILE: NO CREDENTIAL MATERIAL
// IS REPRESENTABLE HERE. There is no `apiToken` field on any OUTPUT type, and
// there cannot be — the schema has none, because a credential input type in
// GraphQL has no output counterpart by construction. `ConnectProviderCredential`
// below is the ONLY type carrying a token, it is an INPUT shape, and it is
// consumed by the service and discarded. Nothing stores it, caches it or
// returns it. If a field carrying a secret ever appears in this file, the
// guarantee the schema makes has been broken on the client side.

// ─── THE CATALOG (FNP-1179) ───

// What a KIND of provider requires. Mirrors `DnsProviderCredentialType`.
//
// ⚠️ THIS IS THE DISCRIMINATOR THE CONNECT FORM SWITCHES ON. A provider
// declares which shape of credential a connection to it must supply, and the
// form renders a role-ARN field or a token field accordingly. Rendering both
// and letting the tenant choose would let them submit a token for Route 53,
// which the adapter rejects at connect time — a failure the form can prevent by
// simply not offering the wrong field.
export type DnsProviderCredentialType = 'ASSUMED_ROLE' | 'API_TOKEN';

// What a kind of provider is capable of AT ALL. Every field non-null: the
// schema is explicit that a provider whose capabilities are unknown does not
// belong in the catalog, because null would be indistinguishable from "cannot".
export type DnsProviderCapabilities = {
    supportedRecordTypes: string[],
    canWriteRecords: boolean,
    canCreateZones: boolean,
    canDeleteZones: boolean,
    canSetNameservers: boolean,
}

export type DnsProvider = {
    // Stable catalog identifier, e.g. 'ROUTE53' / 'CLOUDFLARE'.
    id: string,
    // Human-readable, e.g. 'Amazon Route 53'. The picker shows THIS, never id.
    name: string,
    credentialType: DnsProviderCredentialType,
    // ⚠️ The CEILING. A connection's own snapshot is this narrowed by what the
    // tenant's credential actually proved — never wider.
    capabilities: DnsProviderCapabilities,
}

export type ListDnsProviders = {
    listDnsProviders: {
        items: DnsProvider[],
        nextToken?: string | null,
    }
}

// ─── THE CONNECTION (FNP-1181) ───

// Which shape of credential THIS TENANT ACTUALLY SUPPLIED. A separate enum from
// the catalog's in the schema, and separate here, because they answer different
// questions — what a provider REQUIRES vs what was SUPPLIED.
export type ProviderConnectionCredentialType = 'ASSUMED_ROLE' | 'API_TOKEN';

// ⚠️ THREE STATES, AND `INVALID` IS NOT ALWAYS A MISTAKE.
//
//   ACTIVE      — validated against the provider; the snapshot is real.
//   INVALID     — the provider REJECTED the credential. NOT retryable on its
//                 own: something must change first.
//   UNREACHABLE — the provider could not be REACHED. The credential may be
//                 perfectly good. Retryable as-is.
//
// The schema is emphatic that there is no PENDING state, because creation
// validates before storing. That has a consequence this module is built around:
// a FIRST-TIME ASSUMED_ROLE connection lands INVALID or UNREACHABLE BY DESIGN
// (see `isAwaitingTrustPolicy` below), and rendering that as a user error would
// be wrong. See the module's ExternalIdPanel for the full explanation.
export type ProviderConnectionStatus = 'ACTIVE' | 'INVALID' | 'UNREACHABLE';

// What THIS CONNECTION'S CREDENTIAL proved it could do — the catalog ceiling
// narrowed by the credential. A tenant who handed Flexi a read-only Route 53
// role has a connection that cannot write, and that is a legitimate,
// fully-working connection, not a broken one. The detail view renders it as
// read-only rather than as failure.
export type ProviderConnectionCapabilities = {
    supportedRecordTypes: string[],
    canWriteRecords: boolean,
    canCreateZones: boolean,
    canDeleteZones: boolean,
    canSetNameservers: boolean,
}

// WHERE THE SECRET LIVES. Never the secret.
//
// ⚠️ THE TWO FIELDS HAVE OPPOSITE AUDIENCES AND THIS MODULE TREATS THEM SO.
//
//   secretArn  — infrastructure. An OPERATOR's address for the secret. The
//                schema notes it is not itself sensitive (knowing the address
//                of a safe does not open it), but a TENANT has no use for it
//                and it is deliberately NOT rendered anywhere in this module.
//                It is not even selected in the queries — see services/queries.
//
//   externalId — THE TENANT'S. Flexi MINTS it per connection and the tenant
//                MUST put it in their role's trust policy or the assume-role
//                can never succeed. It is a value Flexi has to TELL them, and
//                surfacing it prominently is the central job of this module's
//                detail view.
//
// Present only for ASSUMED_ROLE connections, hence nullable.
export type ProviderConnectionSecretRef = {
    externalId?: string | null,
}

export type ProviderConnection = {
    // Composite and tenant-scoped: 'PROVIDER_CONNECTION#<accountId>#<uuid>'.
    // ⚠️ OPAQUE. Nothing here parses or splits it; it is passed back verbatim.
    id: string,
    accountId?: string,
    // References DnsProvider.id from the catalog, e.g. 'ROUTE53'.
    providerId: string,
    // What the tenant named it, so two connections to the same provider are
    // tellable apart. Every list and picker shows this, never the id.
    displayName: string,
    credentialType: ProviderConnectionCredentialType,
    status: ProviderConnectionStatus,
    // ⚠️ THE PROVIDER'S OWN WORDS, VERBATIM. Never re-worded on the way to the
    // screen — classify for the core, preserve for the human (FNP-1180).
    // FNP-1087 took a whole investigation because a normalised class was all
    // anyone had.
    statusDetail?: string | null,
    lastValidatedAt?: string | null,
    capabilities: ProviderConnectionCapabilities,
    // ⚠️ FNP-1233, split by FNP-1399 — DECLARED, NOT PROBED. See `dns` on
    // ConnectProviderInput for the full reasoning.
    //
    // Optional HERE rather than the non-null the output type declares, because
    // an API that has not yet shipped the split still comes back without it.
    // Typing it non-null would be a lie the compiler enforces and the runtime
    // breaks on the first such response.
    //
    // ⚠️ Read it through `declaredDnsGrant()`, never directly. An absent block
    // and an absent field must both resolve to NOT-granted — the same
    // direction as an explicit false, so an older API fails closed rather than
    // granting writes by omission.
    dnsCapabilities?: {
        canWriteRecords?: boolean | null,
        canCreateZones?: boolean | null,
        canDeleteZones?: boolean | null,
    } | null,
    secretRef: ProviderConnectionSecretRef,
    createdByUsername?: string | null,
    created?: string | null,
    updated?: string | null,
}

export type ProviderConnectionsConnectionResult = {
    items: ProviderConnection[],
    nextToken?: string | null,
}

export type ListProviderConnections = {
    listProviderConnections: ProviderConnectionsConnectionResult
}

export type GetProviderConnection = {
    getProviderConnection: ProviderConnection
}

// ─── THE WRITE VOCABULARY (FNP-1182) ───

// ⚠️ THE ONLY TYPE IN THIS FILE THAT CARRIES CREDENTIAL MATERIAL, AND IT IS AN
// INPUT. It travels from the form, through the service, into the mutation
// variables, and is never assigned to any state that outlives the submit.
//
// Exactly ONE field is populated, chosen by the provider's credentialType:
//   ASSUMED_ROLE → roleArn    API_TOKEN → apiToken
//
// Both are optional in the schema because the discriminator lives on the
// provider, not on this shape. The form guarantees the right one.
export type ConnectProviderCredential = {
    // The role in the TENANT'S OWN account. Not a secret — it is an identifier,
    // and the detail view shows it back alongside the external id precisely so
    // the tenant can check it against their trust policy.
    roleArn?: string,
    // ⚠️ WRITE-ONLY, ALWAYS. Never rendered, never logged, never echoed back,
    // never put in a query cache. There is no query that could return it.
    apiToken?: string,
}

export type ConnectProviderInput = {
    providerId: string,
    displayName: string,
    credential: ConnectProviderCredential,
    // ⚠️ FNP-1233 — THE TENANT'S CLAIM ABOUT THEIR OWN CREDENTIAL.
    //
    // Write capability is DECLARED, not probed. This is the tenant telling
    // Flexi what the role they created can do — "this role can create and
    // delete zones" — not a Flexi permission being granted and not a request
    // for one. The distinction is the whole reason the label matters: a control
    // reading "enable writes" invites someone to tick it hopefully, and a
    // hopeful tick produces a connection that claims a capability the
    // credential does not have, failing at the first write instead of at the
    // declaration.
    //
    // ⚠️ DEFAULTS OFF, HERE AND SERVER-SIDE. Nullable in the API with a
    // server-side default of false, so a connection nobody deliberately marked
    // writable cannot write. Omitting the field is a valid way to say "no".
    //
    // Sent only for grants the customer actually ticked — see
    // buildConnectInput().
    //
    // ⚠️ FNP-1399 — THREE FIELDS, NOT ONE `writable` BOOLEAN. The drawer asks
    // three questions and this used to send `writeRecords || createZones ||
    // deleteZones`, so a customer who ticked only "Change records" was stored
    // as able to DELETE THEIR HOSTED ZONES.
    dns?: {
        canWriteRecords?: boolean,
        canCreateZones?: boolean,
        canDeleteZones?: boolean,
    },

    // ⚠️ FNP-1363 — THE CERTIFICATE HALF OF THE SAME DECLARATION.
    //
    // `dns` covers DNS. This covers ACM, and it is separate because the
    // tenant decides it separately: someone may let Flexi issue certificates
    // and still keep deletion to themselves. FNP-1280 split canRequest from
    // canDelete for exactly that case.
    //
    // ⚠️ THIS WAS COMPUTED AND DISCARDED UNTIL 2026-09-05. `declarationFor()`
    // has always returned a `certificate` block, and the deployed API has
    // always accepted one, but nothing carried it across — so a tenant could
    // tick "Request TLS certificates", paste a policy granting
    // acm:RequestCertificate, and still get a connection declaring it could
    // not request one. That is what blocked FNP-1193.
    certificate?: {
        canRequest?: boolean,
        canDelete?: boolean,
    },
}

export type ConnectProvider = {
    connectProvider: ProviderConnection
}

// Test a credential and store NOTHING.
//
// ⚠️ THIS CANNOT VALIDATE A REAL ASSUMED_ROLE ASSUMPTION, AND THE MODULE MUST
// NOT IMPLY IT DOES. The external id it mints is a THROWAWAY: never stored,
// never returned. The tenant's trust policy names the id from their REAL
// connection, which does not exist yet at test time — so a Route 53 test can
// only ever come back INVALID for a tenant who has not yet connected.
//
// It is therefore used here for ONE thing: catching a malformed ARN or a
// wrong-shaped credential BEFORE saving. See hooks/useTestProviderCredential
// for the guard that keeps it honest.
export type TestProviderCredentialInput = {
    providerId: string,
    credential: ConnectProviderCredential,
}

// NOT a ProviderConnection — nothing was stored, and a connection-shaped result
// would misrepresent what happened. Same three-state status vocabulary, so one
// component renders a test result and a real connection's health.
export type ProviderCredentialTestResult = {
    ok: boolean,
    status: ProviderConnectionStatus,
    statusDetail?: string | null,
    capabilities: ProviderConnectionCapabilities,
}

export type TestProviderCredential = {
    testProviderCredential: ProviderCredentialTestResult
}

// Replace the credential, KEEP the connection.
//
// ⚠️ THIS IS THE RE-VALIDATE PATH, and it is the whole reason a first INVALID
// is recoverable. An ASSUMED_ROLE connection keeps its external id across a
// rotation, so the tenant fixes their trust policy and re-submits THE SAME
// roleArn — no trust-policy edit is needed on Flexi's side.
//
// No `providerId`: the provider is a property of the connection being rotated,
// and accepting one would let a caller move a connection between providers
// under the guise of a credential change.
export type RotateProviderCredentialInput = {
    id: string,
    credential: ConnectProviderCredential,
    // ⚠️ FNP-1233 — ROTATION IS ALSO THE PROMOTION PATH.
    //
    // This is how a connection becomes writable after its trust policy is
    // fixed: the tenant re-submits the same roleArn and declares what the role
    // can now do. Same semantics and same default-off rule as on connect —
    // see ConnectProviderInput.
    dns?: {
        canWriteRecords?: boolean,
        canCreateZones?: boolean,
        canDeleteZones?: boolean,
    },
}

export type RotateProviderCredential = {
    rotateProviderCredential: ProviderConnection
}

// ─── RE-VALIDATE WITHOUT A CREDENTIAL (FNP-1269) ───

// ⚠️ AN ID AND NOTHING ELSE. THE EMPTINESS IS THE FEATURE.
//
// Re-validating is not a credential change. The tenant connected, Flexi minted
// an external id, they pasted it into their role's trust policy, and now they
// are asking Flexi to look again — the stored credential is the same credential
// and they have nothing new to give. `RotateProviderCredentialInput.credential`
// is non-null, so before this existed the only re-check path made them retype
// their role ARN, and a typo in that retype silently repointed the connection at
// a DIFFERENT role. Worse, nothing ever prefilled the field.
//
// ⚠️ NO `writable`, AND NOT BY OVERSIGHT. Omitting the field is what PRESERVES
// the connection's existing declaration. Adding it here would invite a caller to
// re-send what the tenant last chose, which turns a read-only re-check into a
// capability write — and a tenant who did not re-tick the switch would have a
// capability revoked by a button labelled "re-validate". Same reasoning for the
// certificate capability declarations.
//
// The absence of every field but `id` is the guard, exactly as it is on
// `RenameProviderConnectionInput`.
export type RevalidateProviderConnectionInput = {
    id: string,
}

export type RevalidateProviderConnection = {
    revalidateProviderConnection: ProviderConnection
}

// ─── RENAME (FNP-1304) ───

// ⚠️ THE DISPLAY NAME AND NOTHING ELSE.
//
// A rename is the one edit on this entity that needs no provider round-trip:
// `displayName` is the only field a TENANT asserts, while status, capabilities,
// lastValidatedAt and the certificate declarations are all records of what the
// provider said. So this input carries no credential, no roleArn and no
// capability flags — changing any of those is `rotateProviderCredential`, which
// re-validates. The absence of those fields here is the guard.
export type RenameProviderConnectionInput = {
    id: string,
    displayName: string,
}

export type RenameProviderConnection = {
    renameProviderConnection: ProviderConnection
}

// ─── DECLARE CAPABILITIES (FNP-1400) ───

// Correct what the tenant declared, without touching the credential.
//
// ⚠️ NO `credential` FIELD, AND ITS ABSENCE IS THE GUARANTEE. There is nothing
// on this input that could repoint the connection at another role, which is
// what makes a correction structurally unable to do what a rotation does.
//
// ⚠️ BOTH BLOCKS OPTIONAL, AND AN OMITTED ONE KEEPS THE CURRENT DECLARATION.
// A tenant correcting their DNS grants must not silently reset the certificate
// grants they never mentioned. Inside a supplied block an omitted FIELD keeps
// its value and an explicit `false` revokes — so the caller must send the
// COMPLETE intended state of any block it names, not just the changed field.
export type DeclareProviderCapabilitiesInput = {
    id: string,
    dns?: {
        canWriteRecords?: boolean,
        canCreateZones?: boolean,
        canDeleteZones?: boolean,
    },
    certificate?: {
        canRequest?: boolean,
        canDelete?: boolean,
    },
}

export type DeclareProviderCapabilities = {
    declareProviderCapabilities: ProviderConnection
}

// ─── DISCONNECT (FNP-1182) ───

// What disconnecting WOULD destroy. Read before it is destroyed.
export type ProviderDisconnectPreview = {
    connectionId: string,
    displayName: string,
    providerId: string,
    // The connection's status at preview time, so a broken connection is
    // visibly broken on the confirmation screen rather than looking empty.
    status: ProviderConnectionStatus,
    // ⚠️ NULL IS "UNKNOWN", NOT "ZERO". THIS IS THE MOST DANGEROUS FIELD IN THE
    // MODULE.
    //
    // A broken connection cannot be asked what it manages. Rendering null as
    // "0 zones" would tell a tenant that disconnecting costs them nothing when
    // it might cost them every zone they have — the empty-versus-broken
    // confusion at the one moment it is most expensive. `zoneCountUnavailable`
    // states the unknown explicitly so a client cannot mistake null for "the
    // field was not requested". Everything that renders this branches on the
    // FLAG FIRST — see `zoneImpact()` below, which is the only reader.
    managedZoneCount?: number | null,
    zoneCountUnavailable: boolean,
    secretName: string,
    // Plain sentences, most severe first. Rendered VERBATIM and in order — this
    // is read by a human deciding whether to proceed.
    consequences: string[],
}

export type PreviewProviderDisconnect = {
    previewProviderDisconnect: ProviderDisconnectPreview
}

export type DisconnectProviderInput = {
    id: string,
}

// Every field is an OBSERVED FACT, not an intention: `secretDeleted` is true
// only when Secrets Manager confirmed the delete.
export type DisconnectProviderResult = {
    connectionId: string,
    secretDeleted: boolean,
    secretName: string,
    connectionDeleted: boolean,
}

export type DisconnectProvider = {
    disconnectProvider: DisconnectProviderResult
}

// ─── THE DERIVED VOCABULARY ───
//
// Named states rather than nullability checks scattered across the table, the
// detail view and the disconnect modal. Three call sites branching on the same
// nullable field independently is how two of them end up agreeing and the third
// does not — the lesson the zones module's `delegationState` records.

// THE ZONE-IMPACT TRI-STATE, NAMED.
//
// ⚠️ THE FLAG IS CHECKED BEFORE THE NUMBER, ALWAYS. Reading `managedZoneCount`
// first and treating a null as 0 is the exact defect this type exists to make
// unrepresentable.
export type ZoneImpact =
    | { kind: 'UNKNOWN' }
    | { kind: 'NONE' }
    | { kind: 'COUNTED', count: number };

export const zoneImpact = (preview: ProviderDisconnectPreview): ZoneImpact => {
    // FLAG FIRST. An unavailable count is unknown whatever the number field
    // happens to hold — including when it holds 0.
    if (preview.zoneCountUnavailable) {
        return {kind: 'UNKNOWN'};
    }
    const count = preview.managedZoneCount;
    // A null with the flag UNSET should not happen, but "should not happen" is
    // not a reason to render it as zero. Unknown is the honest answer.
    if (typeof count !== 'number') {
        return {kind: 'UNKNOWN'};
    }
    if (count === 0) {
        return {kind: 'NONE'};
    }
    return {
        kind: 'COUNTED',
        count,
    };
};

/**
 * Whether this connection is a FIRST-TIME ASSUMED_ROLE connection that is
 * simply waiting for the tenant to paste the external id into their trust
 * policy — as opposed to one that is genuinely misconfigured.
 *
 * ⚠️ THIS IS THE CENTRAL DISTINCTION OF THE WHOLE MODULE.
 *
 * Flexi mints the external id SERVER-SIDE during `connectProvider`, derived
 * from the connection id — so it cannot exist before the connection does. The
 * tenant needs it in their role's trust policy before Flexi can assume the
 * role. Therefore the ordering is unavoidable:
 *
 *   1. Connect → stored, status INVALID/UNREACHABLE, externalId now populated.
 *   2. Flexi TELLS the tenant the external id + role ARN.
 *   3. Tenant edits their trust policy, then re-validates (rotate).
 *
 * Step 1 coming back not-ACTIVE is the DESIGNED path, not a failure. Presenting
 * it as "something went wrong" tells the tenant they made a mistake when they
 * did exactly what was asked, and hides the one value they now need.
 *
 * The condition is deliberately NOT time-based or attempt-counted: an
 * ASSUMED_ROLE connection that is not ACTIVE and HAS an external id always has
 * "add this to your trust policy" as its next step, whether it is thirty
 * seconds or thirty days old. A tenant returning to an old broken connection
 * needs that instruction just as much.
 */
export const isAwaitingTrustPolicy = (connection: ProviderConnection): boolean => {
    return connection.credentialType === 'ASSUMED_ROLE'
        && connection.status !== 'ACTIVE'
        && Boolean(connection.secretRef?.externalId)
        // ⚠️ FNP-1285 — AND THE PROVIDER HAS NOT ALREADY REFUSED IT.
        //
        // Without this, every non-ACTIVE ASSUMED_ROLE connection was told to
        // add the external ID to its trust policy, including one whose ARN
        // names a role that does not exist. No trust-policy edit can fix
        // that, so the interface was confidently instructing the tenant to do
        // something impossible, with the provider's own refusal rendered two
        // lines below contradicting it.
        //
        // `statusDetail` carries the provider's verbatim response and is
        // populated only when a validation actually ran and FAILED. Measured
        // against the real rows: the wrong-ARN connection has one, the
        // healthy connection does not. A connection saved and not yet
        // validated against has none, which is the designed first-connect
        // path this state exists for, and it still reads as awaiting.
        //
        // ⚠️ NOT A CLASSIFICATION OF *WHY* IT FAILED, deliberately. STS
        // returns AccessDenied whether the role is absent or merely does not
        // trust us — AWS answers identically so that nobody can enumerate
        // role ARNs in another account. So "the role does not exist" is NOT
        // distinguishable from "the role does not trust Flexi yet", and
        // pretending otherwise would mean string-matching the prose, which
        // FNP-1280 exists to remove. This asks only the question that has an
        // honest answer: has the provider rejected this credential yet?
        && !connection.statusDetail;
};

/**
 * Whether a connection works at all right now.
 *
 * Used to gate the capability rendering: an INVALID connection's snapshot
 * records what the credential proved AT CONNECT TIME, which is not a claim
 * about what it can do NOW. The detail view says so rather than presenting a
 * stale snapshot as current fact.
 */
export const isConnectionUsable = (connection: ProviderConnection): boolean => {
    return connection.status === 'ACTIVE';
};

/**
 * Whether a not-ACTIVE status can plausibly be fixed by retrying AS-IS.
 *
 * UNREACHABLE means the provider could not be reached — the credential may be
 * perfectly good, so re-validating unchanged is a sensible thing to offer.
 * INVALID means the provider REJECTED it: clicking again changes nothing, and
 * the tenant must fix something (usually the trust policy) first. The
 * re-validate control words itself differently for the two.
 */
export const isStatusRetryableAsIs = (status: ProviderConnectionStatus): boolean => {
    return status === 'UNREACHABLE';
};

// ─── FNP-1233: DECLARED WRITE CAPABILITY ───

/**
 * Whether this connection was DECLARED writable by the tenant.
 *
 * ⚠️ FAILS CLOSED ON EVERY UNKNOWN, AND THAT IS THE POINT.
 *
 * Three inputs collapse to false: an explicit `false`, a `null`, and —
 * critically — `undefined`, which is what every connection currently returns
 * because `writable` is NOT in the deployed schema yet. Reading the field
 * directly (`if (connection.writable)`) would happen to behave correctly today
 * and start granting writes the moment someone changed the type to non-null
 * without checking the API. Routing every read through here makes the
 * fail-closed direction a property of the module rather than of each call site.
 *
 * ⚠️ This answers "did the tenant claim this credential can write", NOT "does
 * Flexi permit writing". Nothing here grants anything.
 */
export const isWritableDeclared = (connection: ProviderConnection): boolean => {
    // ⚠️ FNP-1399 — THIS NOW ANSWERS "MAY IT CHANGE RECORDS", SPECIFICALLY.
    //
    // It used to read one `writable` boolean that was the OR of record writes,
    // zone creation and zone deletion. Every caller of this helper is asking
    // about RECORD writes — the record form, the add-record button, the
    // certificate validation path — so reading `canWriteRecords` is what they
    // all meant. A caller that wants a zone grant asks for it by name through
    // `declaredDnsGrant`; none of them wants the OR.
    return declaredDnsGrant(connection, 'canWriteRecords');
};

/**
 * FNP-1399 — read ONE declared DNS grant, failing closed.
 *
 * ⚠️ FAILS CLOSED ON EVERY UNKNOWN, per field. An absent block, an absent
 * field, a null and an explicit false all resolve to NOT granted, so an API
 * that has not shipped the split yet — or a row that predates it — refuses
 * rather than granting by omission. Only a literal `true` grants.
 *
 * Reading the block directly would happen to behave correctly today and start
 * granting the moment someone typed a field non-null without checking. Routing
 * every read through here makes the fail-closed direction a property of the
 * module rather than of each call site.
 */
export const declaredDnsGrant = (
    connection: ProviderConnection,
    grant: 'canWriteRecords' | 'canCreateZones' | 'canDeleteZones',
): boolean => {
    return connection.dnsCapabilities?.[grant] === true;
};

/**
 * Build the `connectProvider` input, omitting each declaration unless it is
 * affirmative.
 *
 * ⚠️ OMISSION IS HOW THIS API SPELLS "NO", and that is the API's own contract:
 * every declaration field is nullable in with a server-side default of false,
 * so "the caller said nothing" and "the caller said no" land on the same safe
 * answer. Sending `false` explicitly would be equivalent, but saying nothing is
 * the shape the schema was designed around and the one the server documents.
 *
 * ⚠️ FNP-1399 — `ConnectProviderInput` CARRIES `dns`, NOT `writable`. Verified
 * live against the dev API (`ia35gxdm75dbfabnw5jbvrhgem`) on 2026-09-05 after
 * the split deployed: the introspected schema has
 * `dnsCapabilities: ProviderConnectionDnsCapabilities!` and no `writable`
 * anywhere. TEST STILL SERVES THE OLD SHAPE until this ships through the
 * pipeline.
 *
 * ⚠️ EACH BLOCK IS OMITTED WHOLE WHEN NOTHING IS GRANTED, rather
 * than sent as `{canRequest: false, canDelete: false}`. Same reason: an absent
 * block is the API's "no", and it keeps a read-only connect request carrying no
 * declarations at all, which is what a read-only connection means.
 */
export const buildConnectInput = (
    providerId: string,
    displayName: string,
    credential: ConnectProviderCredential,
    dns: {canWriteRecords: boolean, canCreateZones: boolean, canDeleteZones: boolean},
    certificate?: {canRequest: boolean, canDelete: boolean},
): ConnectProviderInput => {
    const input: ConnectProviderInput = {
        providerId,
        displayName,
        credential,
    };
    // ⚠️ FNP-1399 — EACH GRANT TRAVELS ON ITS OWN, and only when ticked. The
    // block is omitted entirely when nothing was granted, which is the API's
    // "no" and keeps a read-only connect request carrying no declarations at
    // all. Sending `{canWriteRecords: false, ...}` would say the same thing
    // more loudly, but an OR across the three would say something false.
    if (dns.canWriteRecords || dns.canCreateZones || dns.canDeleteZones) {
        input.dns = {};
        if (dns.canWriteRecords) {
            input.dns.canWriteRecords = true;
        }
        if (dns.canCreateZones) {
            input.dns.canCreateZones = true;
        }
        if (dns.canDeleteZones) {
            input.dns.canDeleteZones = true;
        }
    }
    // Only affirmative grants travel; a block of all-false says nothing the
    // server does not already assume.
    if (certificate?.canRequest || certificate?.canDelete) {
        input.certificate = {};
        if (certificate.canRequest) {
            input.certificate.canRequest = true;
        }
        if (certificate.canDelete) {
            input.certificate.canDelete = true;
        }
    }
    return input;
};

/**
 * FNP-1400 — build the `declareProviderCapabilities` input from a correction.
 *
 * ⚠️ THIS SENDS THE COMPLETE STATE OF BOTH BLOCKS, INCLUDING THE FALSES, and
 * that is the opposite of `buildConnectInput`'s omit-unless-affirmative rule.
 * The difference is intent, and getting it backwards silently breaks the
 * feature:
 *
 *   · On CONNECT there is nothing to keep. An omitted field means "not
 *     granted", so omitting the falses says exactly what is meant.
 *   · On CORRECT the API reads an omitted field as "keep what is stored". A
 *     tenant unticking "delete zones" is stating a REVOCATION, and sending
 *     nothing for it would keep the old `true` — the correction would appear to
 *     succeed and change nothing, which is worse than failing.
 *
 * So a correction is a full statement of the intended state, and the explicit
 * `false` is the revocation the API's `=== undefined` check is waiting for.
 *
 * ⚠️ THE CERTIFICATE BLOCK IS DELIBERATELY NOT SENT, AND OMITTING IT IS THE
 * SAFE DIRECTION. `ProviderConnection` in this app has no
 * `certificateCapabilities` field yet — the queries select it but nothing ever
 * typed it, which is FNP-1398 — so the app cannot READ the tenant's current ACM
 * declaration. Sending a block it cannot read would state
 * `{canRequest: false, canDelete: false}` on every correction and REVOKE
 * certificate grants the tenant never touched, which is precisely the
 * silent-revocation failure this function's own rule above exists to prevent.
 *
 * An omitted block means "said nothing", so the stored certificate declaration
 * survives untouched. When FNP-1398 makes the field readable, this builder
 * gains the block and the drawer gains the two ticks — together, in one change.
 */
export const buildDeclareInput = (
    id: string,
    grants: RoleGrantSelection,
): DeclareProviderCapabilitiesInput => ({
    id,
    // ⚠️ THROUGH `declarationFor`, NOT RE-DERIVED HERE. FNP-1376 requires that
    // exactly ONE function turns a set of ticks into a declaration, so connect
    // and correct cannot map the same checkbox to different fields. Writing
    // `canWriteRecords: grants.writeRecords` inline would be a second producer
    // that agrees today and drifts the first time a grant is added.
    dns: declarationFor(grants).dns,
});

/**
 * Build the `rotateProviderCredential` input. Same omission rule, same reason
 * as `buildConnectInput` — see there.
 */
export const buildRotateInput = (
    id: string,
    credential: ConnectProviderCredential,
    dns: {canWriteRecords: boolean, canCreateZones: boolean, canDeleteZones: boolean},
): RotateProviderCredentialInput => {
    const input: RotateProviderCredentialInput = {
        id,
        credential,
    };
    if (dns.canWriteRecords || dns.canCreateZones || dns.canDeleteZones) {
        input.dns = {};
        if (dns.canWriteRecords) {
            input.dns.canWriteRecords = true;
        }
        if (dns.canCreateZones) {
            input.dns.canCreateZones = true;
        }
        if (dns.canDeleteZones) {
            input.dns.canDeleteZones = true;
        }
    }
    return input;
};
